import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { useCollection } from "@/hooks/useCollection";
import { useCouple } from "@/hooks/useCouple";
import { useSuggestions } from "@/hooks/useSummary";
import { Chips, OwnerPicker } from "@/components/shared/Primitives";
import { TX_TYPES } from "@/types";
import { parseAmount, todayISO } from "@/lib/format";
import { cn } from "@/lib/utils";

export const QuickEntrySheet = ({ open, onOpenChange, defaultType = "gasto", defaultGoalId, transaction }) => {
  const { me, partner } = useCouple();
  const tx = useCollection("transactions", { month: todayISO().slice(0, 7) });
  const cats = useCollection("categories");
  const goals = useCollection("goals");
  const { data: suggestions = [] } = useSuggestions();
  const [type, setType] = useState(defaultType);
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayISO());
  const [categoryId, setCategoryId] = useState(null);
  const [goalId, setGoalId] = useState(defaultGoalId || null);
  const [owner, setOwner] = useState("nosso");
  const [note, setNote] = useState("");
  const [more, setMore] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (transaction) {
      setType(transaction.type); setAmount(String(transaction.amount).replace(".", ",")); setDate(transaction.date);
      setCategoryId(transaction.category_id || null); setGoalId(transaction.goal_id || null); setOwner(transaction.owner || "nosso");
      setNote(transaction.note || ""); setMore(true);
    } else {
      setType(defaultType); setAmount(""); setDate(todayISO()); setCategoryId(null); setGoalId(defaultGoalId || null); setOwner("nosso"); setNote(""); setMore(false);
    }
  }, [open, defaultType, defaultGoalId, transaction]);

  const value = parseAmount(amount);
  const canSave = value > 0 && !!date && (type !== "gasto" || categoryId) && (type !== "aporte" || goalId || goals.items.length === 0);

  const save = async () => {
    const payload = {
      type, amount: value, date, category_id: type === "gasto" ? categoryId : null,
      goal_id: type === "aporte" ? goalId : null, owner, responsible_id: owner === "meu" ? me?.id : owner === "seu" ? partner?.id || null : null, note: note || null,
    };
    if (transaction) {
      await tx.update.mutateAsync({ id: transaction.id, ...payload });
      toast.success("Corrigido. Sem drama.");
    } else {
      await tx.create.mutateAsync(payload);
      toast.success(type === "aporte" ? "Aporte guardado no futuro" : type === "entrada" ? "Entrada registrada" : "Salvo. Sem drama.");
    }
    onOpenChange(false);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="quick-entry-sheet" className="bg-[var(--cream)] border-[var(--line)] max-w-md mx-auto rounded-t-[28px]">
        <div className="px-5 pb-[max(env(safe-area-inset-bottom),20px)] pt-2">
          <DrawerTitle className="sr-only">{transaction ? "Editar lançamento" : "Lançamento rápido"}</DrawerTitle>
          {transaction && <p className="eyebrow text-center mb-3">corrigindo lançamento</p>}
          <div className="grid grid-cols-3 gap-2 mb-6" data-testid="quick-entry-type">
            {TX_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                data-testid={`quick-entry-type-${t.value}`}
                onClick={() => setType(t.value)}
                className={cn("h-12 rounded-2xl condensed text-lg tracking-wider border transition-colors",
                  type === t.value ? "bg-[var(--coffee)] text-[var(--cream)] border-[var(--coffee)]" : "border-[var(--line)] text-[var(--coffee-medium)]")}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex items-baseline gap-2 justify-center mb-6">
            <span className="serif text-3xl text-[var(--coffee-muted)]">R$</span>
            <input
              autoFocus
              inputMode="decimal"
              placeholder="0,00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="quick-entry-amount-input"
              className="num bg-transparent outline-none text-5xl font-semibold w-[220px] text-[var(--coffee)] placeholder:text-[var(--mask)]"
            />
          </div>

          {type === "gasto" && !transaction && suggestions.length > 0 && !amount && (
            <div className="mb-5" data-testid="quick-entry-suggestions">
              <p className="eyebrow text-center mb-2">de novo?</p>
              <div className="flex flex-wrap gap-2 justify-center">
                {suggestions.map((sg) => (
                  <button key={sg.note} type="button" className="chip h-10 px-3 gap-2" data-testid="quick-entry-suggestion"
                    onClick={() => { setAmount(String(sg.amount).replace(".", ",")); setCategoryId(sg.category_id || null); setNote(sg.note); setOwner(sg.owner || "nosso"); }}>
                    <span>{sg.note}</span><span className="num text-[var(--coffee-muted)]">~{Math.round(sg.amount)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {type === "gasto" && (
            <div className="flex flex-wrap gap-2 justify-center mb-5" data-testid="quick-entry-categories">
              {cats.items.map((c) => (
                <button key={c.id} type="button" className="chip h-10 px-4" data-active={categoryId === c.id}
                  data-testid={`quick-entry-category-${c.id}`} onClick={() => setCategoryId(c.id)}>
                  {c.name}
                </button>
              ))}
            </div>
          )}
          {type === "aporte" && (
            <div className="flex flex-wrap gap-2 justify-center mb-5" data-testid="quick-entry-goals">
              {goals.items.length === 0 && <p className="text-sm text-[var(--coffee-muted)]">Sem plano ainda — vai como “futuro” geral.</p>}
              {goals.items.map((g) => (
                <button key={g.id} type="button" className="chip h-10 px-4" data-active={goalId === g.id}
                  data-testid={`quick-entry-goal-${g.id}`} onClick={() => setGoalId(g.id)}>
                  {g.title}
                </button>
              ))}
            </div>
          )}

          {more ? (
            <div className="space-y-3 mb-5 rise">
              <OwnerPicker value={owner} onChange={setOwner} testId="quick-entry-owner" />
              <div className="flex gap-2">
                <input type="date" className="field num flex-1" value={date} onChange={(e) => setDate(e.target.value)} data-testid="quick-entry-date-input" />
                <input className="field flex-[2]" placeholder="uma nota curta (opcional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="quick-entry-note-input" />
              </div>
            </div>
          ) : (
            <button type="button" className="btn-ghost w-full mb-3" onClick={() => setMore(true)} data-testid="quick-entry-more-button">
              meu / seu / nosso · data · nota
            </button>
          )}

          <button className="btn-primary w-full h-14 text-base" disabled={!canSave || tx.create.isPending || tx.update.isPending} onClick={save} data-testid="quick-entry-submit-button">
            {transaction ? "Salvar correção" : "Salvar"}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
